import { Box, Button, Card, CardContent, Grid, Paper, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as React from "react";
import { useState, useMemo } from "react";
import { subDays } from "date-fns";
import UnfulfilledChannelOrders from "./UnfulfilledChannelOrders";
import FulfilledChannelOrders from "./FulfilledChannelOrders";
import PageTitle from "../../components/PageTitle";
import { FormProvider, RHFTextField, RHFDateRangePickerField } from "../../common/hook-form";
import RHFSelectField from "../../common/hook-form/RHFSelectField";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { useForm } from "react-hook-form";
import useTabParam from "../../hooks/useTabParam";
import { FieldLabel, FilterField } from "../../common/FilterBar";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import palette from "../../../theme/palette";
import { formatCurrency, formatNumber } from "../../common/util";

const PILLS = [
  { key: "1", label: "Unfulfilled", icon: <PendingActionsOutlinedIcon sx={{ fontSize: 18 }} />, main: "#1565c0" },
  { key: "2", label: "Fulfilled", icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />, main: "#1565c0" },
];

const STATUS_OPTIONS = [
  { label: "Created", value: "CREATED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "On Hold", value: "HOLD" },
  { label: "Released", value: "RELEASE" },
  { label: "Partially Fulfilled", value: "PARTIALLY_FULLFILLED" },
  { label: "Deleted", value: "DELETED" },
];

const CHANNEL_OPTIONS = [
  { label: "Shopify", value: "SHOPIFY" },
  { label: "WooCommerce", value: "WOOCOMMERCE" },
  { label: "Unicommerce", value: "UNICOMMERCE" },
  { label: "Magento", value: "MAGENTO" },
  { label: "Amazon", value: "AMAZON" },
  { label: "Flipkart", value: "FLIPKART" },
];

// ── KPI card style — reusable across pages ──────────────────────────────────
const kpiCardSx = (color) => ({
  p: 1.5, borderRadius: '10px',
  border: `1px solid ${palette.grey[300]}`,
  borderTop: `3px solid ${color}`,
  textAlign: 'center',
  // Equal height + centered content so cards stay consistent whether or not they
  // carry a sub-line (e.g. COD/Prepaid %) — Grid rows stretch to the tallest.
  height: '100%',
  minHeight: 96,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.25,
});

function ChannelOrders() {
  const [value, setValue] = useTabParam("1");
  const [filterChange, setFiltersChange] = useState(0);
  const [filters, setFilters] = useState({});
  // Track current page data for KPIs (set by child components)
  const [pageData, setPageData] = useState([]);

  const methods = useForm({
    defaultValues: {
      order_number: "",
      order_name: "",
      merchant: "",
      awb_number: "",
      channel: "",
      status: "",
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
    },
  });
  const { handleSubmit, reset, watch } = methods;

  const onSubmit = async (formData) => {
    setFiltersChange((f) => f + 1);
    setFilters(formData);
  };

  const merchantDetail = watch("merchant");

  // ── KPI computation from current page rows ────────────────────────────────
  const kpis = useMemo(() => {
    if (!pageData || pageData.length === 0) return null;
    const total = pageData.length;
    const totalValue = pageData.reduce((s, r) => s + (r.total_price || 0), 0);
    const codCount = pageData.filter((r) => (r.payment_mode || r.paymentMode || '').toUpperCase() === 'COD').length;
    const prepaidCount = total - codCount;

    if (value === '1') {
      // Unfulfilled — show by status
      const created = pageData.filter((r) => r.channelOrderStatus === 'CREATED').length;
      const cancelled = pageData.filter((r) => r.channelOrderStatus === 'CANCELLED').length;
      const onHold = pageData.filter((r) => r.channelOrderStatus === 'HOLD').length;
      const other = total - created - cancelled - onHold;
      return { total, totalValue, codCount, prepaidCount, created, cancelled, onHold, other };
    }
    // Fulfilled
    const codOnHold = pageData.filter((r) => r.isCodOnHold).length;
    return { total, totalValue, codCount, prepaidCount, codOnHold };
  }, [pageData, value]);

  return (
    <>
      <PageTitle>Channel Orders</PageTitle>

      {/* ── Help text ──────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        mb: 2, px: 2, py: 1.5,
        borderRadius: '8px',
        border: `1px solid ${palette.info.light}`,
        bgcolor: palette.info.lighter,
      }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: palette.info.main, mt: '1px', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: palette.grey[700], lineHeight: 1.6 }}>
          Channel Orders are orders pulled directly from connected webstores (Shopify, WooCommerce, Unicommerce, etc.).
          These orders may or may not be converted into ProShip shipments.
          <strong> Unfulfilled</strong> orders are awaiting shipment creation, while <strong>Fulfilled</strong> orders have been mapped to a ProShip AWB.
        </Typography>
      </Box>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <Card sx={{ mb: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", borderRadius: "8px" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}>

              <FilterField>
                <FieldLabel>Merchant</FieldLabel>
                <MerchantFilterCached fullWidth />
              </FilterField>

              <FilterField>
                <FieldLabel>Order Number</FieldLabel>
                <RHFTextField name="order_number" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
              </FilterField>

              <FilterField>
                <FieldLabel>Order Name</FieldLabel>
                <RHFTextField name="order_name" label="" placeholder="Order Name" fullWidth size="small" InputLabelProps={{ shrink: true }} />
              </FilterField>

              {value === "2" && (
                <FilterField>
                  <FieldLabel>AWB Number</FieldLabel>
                  <RHFTextField name="awb_number" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                </FilterField>
              )}

              <FilterField>
                <FieldLabel>Channel</FieldLabel>
                <RHFSelectField name="channel" label="" placeholder="All Channels" width="100%" size="small" displayEmpty options={CHANNEL_OPTIONS} />
              </FilterField>

              {value === "1" && (
                <FilterField>
                  <FieldLabel>Status</FieldLabel>
                  <RHFSelectField name="status" label="" placeholder="All Statuses" width="100%" size="small" displayEmpty options={STATUS_OPTIONS} />
                </FilterField>
              )}

              <FilterField>
                <FieldLabel>Date Range</FieldLabel>
                <RHFDateRangePickerField name="fromDate" label="" placeholder="Select Date Range" size="small" maxDate={new Date()} />
              </FilterField>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: "0 0 auto" }}>
                <Button type="submit" variant="contained" size="small"
                  sx={{ bgcolor: palette.primary.main, color: palette.common.white, fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", px: 2.5, py: 1, "&:hover": { bgcolor: palette.primary.dark } }}>
                  Filter
                </Button>
                <Button type="button" variant="contained" size="small"
                  sx={{ bgcolor: palette.error.main, color: palette.common.white, fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", px: 2.5, py: 1, "&:hover": { bgcolor: palette.error.dark } }}
                  onClick={() => {
                    reset({ order_number: "", order_name: "", merchant: "", awb_number: "", channel: "", status: "", fromDate: subDays(new Date(), 7), toDate: new Date() });
                    setFilters({});
                    setFiltersChange((f) => f + 1);
                  }}>
                  Reset
                </Button>
              </Box>

            </Box>
          </FormProvider>
        </CardContent>
      </Card>

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      {kpis && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.72rem', color: palette.grey[500], mb: 1 }}>
            Showing metrics for {formatNumber(kpis.total)} orders on this page
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4} md={2}>
              <Paper elevation={0} sx={kpiCardSx(palette.primary.main)}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.primary.main, lineHeight: 1.2 }}>{formatNumber(kpis.total)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Orders</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper elevation={0} sx={kpiCardSx(palette.success.dark)}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.success.dark, lineHeight: 1.2 }}>{formatCurrency(kpis.totalValue)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Total Value</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper elevation={0} sx={kpiCardSx(palette.warning.dark)}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.warning.dark, lineHeight: 1.2 }}>{formatNumber(kpis.codCount)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>COD</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: palette.grey[500] }}>{kpis.total ? `${Math.round((kpis.codCount / kpis.total) * 100)}%` : '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper elevation={0} sx={kpiCardSx(palette.info.main)}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.info.main, lineHeight: 1.2 }}>{formatNumber(kpis.prepaidCount)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Prepaid</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: palette.grey[500] }}>{kpis.total ? `${Math.round((kpis.prepaidCount / kpis.total) * 100)}%` : '—'}</Typography>
              </Paper>
            </Grid>
            {value === '1' && kpis.created !== undefined && (
              <>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={0} sx={kpiCardSx(palette.success.main)}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.success.main, lineHeight: 1.2 }}>{formatNumber(kpis.created)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Created</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={0} sx={kpiCardSx(palette.error.main)}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.error.main, lineHeight: 1.2 }}>{formatNumber(kpis.cancelled + kpis.onHold)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Cancelled / Hold</Typography>
                  </Paper>
                </Grid>
              </>
            )}
            {value === '2' && kpis.codOnHold !== undefined && (
              <>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={0} sx={kpiCardSx(palette.error.main)}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.error.main, lineHeight: 1.2 }}>{formatNumber(kpis.codOnHold)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>COD On Hold</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={0} sx={kpiCardSx(palette.success.main)}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: palette.success.main, lineHeight: 1.2 }}>{formatNumber(kpis.total - kpis.codOnHold)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: palette.grey[600], textTransform: 'uppercase' }}>Released</Typography>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* ── Tab switcher — bold filled active vs outlined inactive, so it clearly reads as tabs ── */}
      <Box sx={{ display: 'inline-flex', gap: 1.25, mb: 2 }}>
        {PILLS.map((t) => {
          const active = value === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => setValue(t.key)}
              role="tab"
              aria-selected={active}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.9, cursor: 'pointer',
                px: 2.75, py: 1.15, borderRadius: '10px', fontSize: 13.5, whiteSpace: 'nowrap',
                fontFamily: 'Poppins, sans-serif', fontWeight: 700, letterSpacing: '0.2px',
                transition: 'all 0.15s ease', border: '1.5px solid',
                borderColor: active ? t.main : '#D5DBE3',
                color: active ? '#fff' : '#64748b',
                bgcolor: active ? t.main : '#fff',
                boxShadow: active ? `0 4px 12px ${t.main}40` : '0 1px 2px rgba(15,23,42,0.05)',
                '&:hover': active ? { filter: 'brightness(0.96)' } : { borderColor: t.main, color: t.main, bgcolor: `${t.main}0d` },
              }}
            >
              {t.icon}
              <Box component='span'>{t.label}</Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Tab content ─────────────────────────────────────────────────── */}
      {value === "1" && (
        <UnfulfilledChannelOrders filters={filters} filterChange={filterChange} onPageData={setPageData} />
      )}
      {value === "2" && (
        <FulfilledChannelOrders filters={filters} filterChange={filterChange} merchantDetail={merchantDetail} onPageData={setPageData} />
      )}
    </>
  );
}

export default ChannelOrders;
